"use client";

import React, { useEffect, useState } from "react";
import SettingsPopup from "@/app/components/SettingsPopup";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { AppConstants } from "@/app/lib/util/AppConstants";
import useInitializeUserFromCookie from "@/app/components/hooks/useInitializeUserFromCookie";
import Link from "next/link";
import {
  goToPage,
  getHomeRouteForRole,
  getSettingsFromSession,
  setFontSizes,
} from "@/app/lib/util/utilities.client";
import useCollapseAction from "@/app/components/hooks/useCollapseAction";
import ContactUsButton from "@/app/components/form_elements/ContactUsButton";
import SafeAvatar from "@/app/components/SafeAvatar";
import { TraineeHeaderMoreMenu } from "@/app/trainee/components/TraineeHeaderMoreMenu";
import NotificationBell from "@/app/components/notifications/NotificationBell";
import "@/app/styles/chat/_main-header.scss";

export const MainHeaderComponent = () => {
  const [isSettingsDisplayed, setIsSettingsDisplayed] = useState(false);
  const [isHeaderDisplayed, setIsHeaderDisplayed] = useState(false);
  const [isActionsDisplayed, setIsActionsDisplayed] = useState(true);
  const currentUrl = usePathname() as string;
  const router = useRouter();
  let currentLoggedInUser = useInitializeUserFromCookie(currentUrl);
  const toggleLayout = useCollapseAction();

  useEffect(() => {
    // Hide header on login pages including /login/test
    if (
      currentUrl === AppConstants.RouteUrls.LOGIN ||
      currentUrl === "/login/test" ||
      currentUrl.startsWith("/login/")
    ) {
      setIsHeaderDisplayed(false);
      setIsActionsDisplayed(false);
    } else if (
      currentLoggedInUser &&
      currentUrl !== AppConstants.RouteUrls.LOGIN
    ) {
      setIsHeaderDisplayed(true);
      setIsActionsDisplayed(true);
    } else {
      if (
        currentUrl.includes(AppConstants.RouteUrls.REGISTER) ||
        (currentUrl.includes(AppConstants.RouteUrls.TRIAL) &&
          currentUrl !== "/")
      ) {
        setIsHeaderDisplayed(true);
        setIsActionsDisplayed(false);
      } else {
        setIsHeaderDisplayed(false);
        setIsActionsDisplayed(false);
      }
    }
  }, [
    currentUrl,
    isActionsDisplayed,
    isHeaderDisplayed,
    isSettingsDisplayed,
    currentLoggedInUser,
    setIsHeaderDisplayed,
    setIsActionsDisplayed,
    setIsSettingsDisplayed,
  ]);

  useEffect(() => {
    // Apply persisted settings globally (even if SettingsPopup isn't open).
    setFontSizes(getSettingsFromSession());
  }, [currentUrl]);

  const useStreamHeaderTheme = currentUrl.startsWith("/trainee");
  const homeRoute = getHomeRouteForRole(currentLoggedInUser?.role);

  return (
    <>
      {isHeaderDisplayed && (
        <header
          className={[
            "k-main-header w-full max-sm:!w-[100vw] z-10",
            useStreamHeaderTheme
              ? "k-main-header--stream overflow-visible"
              : "overflow-x-hidden overflow-y-hidden",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <div className="k-main-header__inner">
            <div className="k-main-header__brand max-sm:p-4 max-sm:w-full md:mx-10">
              <Link
                href={homeRoute}
                className="k-main-header__brand-link hover:cursor-pointer text-primary"
                onClick={(e) => {
                  if (currentLoggedInUser?.role) {
                    e.preventDefault();
                    goToPage(
                      router,
                      getHomeRouteForRole(currentLoggedInUser.role),
                    );
                  }
                }}
              >
                <Image alt="Kovaad" src="/logo.svg" width={40} height={40} />
                <span className="k-main-header__brand-name brand-title-font hidden lg:block">
                  Ko VaaD
                </span>
              </Link>
              {currentLoggedInUser?.role &&
                currentLoggedInUser.role !== AppConstants.Roles.TRAINEE && (
                  <div className="hidden md:flex items-center px-3 py-1 bg-color-blue-light text-color-primary text-sm font-semibold rounded-full ml-4 whitespace-nowrap capitalize">
                    {currentLoggedInUser.role.replace(/_/g, " ")}
                  </div>
                )}
            </div>

            {isActionsDisplayed && (
              <div className="k-main-header__actions mr-4 px-5">
                {useStreamHeaderTheme ? (
                  <>
                    <NotificationBell />
                    <button
                      type="button"
                      className="k-main-header__menu-btn"
                      onClick={() => toggleLayout()}
                      aria-label="Contacts"
                    >
                      <Image
                        src="/hamburger-icon.svg"
                        alt=""
                        width={30}
                        height={30}
                      />
                    </button>
                    <TraineeHeaderMoreMenu
                      onOpenSettings={() => setIsSettingsDisplayed(true)}
                    />
                  </>
                ) : (
                  <>
                    <NotificationBell />
                    <button
                      id="settings"
                      type="button"
                      className="k-main-header__icon-btn k-main-header__icon-btn--settings hidden md:flex"
                      onClick={() => setIsSettingsDisplayed(true)}
                      aria-label="Settings"
                    >
                      <Image
                        src="/gear-icon.svg"
                        alt=""
                        width={24}
                        height={24}
                        style={{ width: "100%", height: "auto" }}
                      />
                    </button>
                    {!currentUrl.includes(AppConstants.RouteUrls.TRIAL) && (
                      <Link
                        id="profile"
                        href={
                          currentLoggedInUser &&
                          currentLoggedInUser.role ===
                            AppConstants.Roles.TRAINEE
                            ? AppConstants.RouteUrls.TRAINEE_PROFILE
                            : AppConstants.RouteUrls.PROFILE
                        }
                        className="k-main-header__icon-btn k-main-header__icon-btn--profile hidden md:flex"
                        onClick={(e) => {
                          e.preventDefault();
                          goToPage(
                            router,
                            currentLoggedInUser &&
                              currentLoggedInUser.role ===
                                AppConstants.Roles.TRAINEE
                              ? AppConstants.RouteUrls.TRAINEE_PROFILE
                              : AppConstants.RouteUrls.PROFILE,
                          );
                        }}
                        aria-label="Profile"
                      >
                        {currentLoggedInUser ? (
                          <SafeAvatar
                            src={currentLoggedInUser.img_url}
                            name={`${currentLoggedInUser.first_name || ""} ${currentLoggedInUser.last_name || ""}`.trim()}
                          />
                        ) : (
                          <Image
                            src="/user-icon.svg"
                            alt=""
                            width={20}
                            height={20}
                          />
                        )}
                      </Link>
                    )}
                    <button
                      type="button"
                      className="k-main-header__menu-btn"
                      onClick={() => toggleLayout()}
                      aria-label="Menu"
                    >
                      <Image
                        src="/hamburger-icon.svg"
                        alt=""
                        width={30}
                        height={30}
                      />
                    </button>
                  </>
                )}
              </div>
            )}
            {!isActionsDisplayed && (
              <ContactUsButton label={AppConstants.CONTACT_US.LABEL} />
            )}
          </div>
        </header>
      )}
      {isSettingsDisplayed && isHeaderDisplayed && (
        <SettingsPopup
          setIsSettingsDisplayed={setIsSettingsDisplayed}
          isSettingsDisplayed={isSettingsDisplayed}
        />
      )}
    </>
  );
};
